/** Persist “field campaigns on explore map” engagement across reloads (sessionStorage). */

export const FIELD_CAMPAIGNS_EXPLORE_ENGAGED_KEY = 'coordal-field-campaigns-explore-engaged';

/** When set, restoring `/` reload re-applies `?campaign_id=` (until user exits workspace). */
export const CAMPAIGN_WORKSPACE_RESUME_ID_KEY = 'coordal-campaign-workspace-resume-id';
export const CAMPAIGN_WORKSPACE_RESUME_ZONE_KEY = 'coordal-campaign-workspace-resume-zone-id';

/** While set, campaign session stays active but explore home chrome is shown. */
export const CAMPAIGN_HOME_OVERLAY_KEY = 'coordal-campaign-home-overlay';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isLikelyCampaignId(value: string): boolean {
  return UUID_RE.test(value.trim());
}

export function readFieldCampaignsExploreEngagedFromSession(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    if (new URLSearchParams(window.location.search).get('campaign_id')) return false;
    if (sessionStorage.getItem(CAMPAIGN_WORKSPACE_RESUME_ID_KEY)) return false;
    if (!localStorage.getItem('auth_token')) return false;
    return sessionStorage.getItem(FIELD_CAMPAIGNS_EXPLORE_ENGAGED_KEY) === '1';
  } catch {
    return false;
  }
}

export function setFieldCampaignsExploreEngagedInSession(engaged: boolean): void {
  try {
    if (engaged) {
      sessionStorage.setItem(FIELD_CAMPAIGNS_EXPLORE_ENGAGED_KEY, '1');
    } else {
      sessionStorage.removeItem(FIELD_CAMPAIGNS_EXPLORE_ENGAGED_KEY);
    }
  } catch {
    /* private mode / quota */
  }
}

export function clearFieldCampaignsExploreEngagedFromSession(): void {
  try {
    sessionStorage.removeItem(FIELD_CAMPAIGNS_EXPLORE_ENGAGED_KEY);
  } catch {
    /* noop */
  }
}

/** Resume id when URL has no `campaign_id` (same tab reload on `/`). */
export function readStoredCampaignWorkspaceResumeId(): string | null {
  try {
    if (typeof window === 'undefined') return null;
    if (!localStorage.getItem('auth_token')) return null;
    const raw = sessionStorage.getItem(CAMPAIGN_WORKSPACE_RESUME_ID_KEY)?.trim();
    if (!raw || !isLikelyCampaignId(raw)) return null;
    return raw;
  } catch {
    return null;
  }
}

export function setCampaignWorkspaceResumeIdInSession(campaignId: string | null, zoneId?: string | null): void {
  try {
    if (campaignId && isLikelyCampaignId(campaignId)) {
      sessionStorage.setItem(CAMPAIGN_WORKSPACE_RESUME_ID_KEY, campaignId);
    } else {
      sessionStorage.removeItem(CAMPAIGN_WORKSPACE_RESUME_ID_KEY);
    }
    if (zoneId && zoneId.trim()) {
      sessionStorage.setItem(CAMPAIGN_WORKSPACE_RESUME_ZONE_KEY, zoneId.trim());
    } else {
      sessionStorage.removeItem(CAMPAIGN_WORKSPACE_RESUME_ZONE_KEY);
    }
  } catch {
    /* noop */
  }
}

export function readStoredCampaignWorkspaceResumeZoneId(): string | null {
  try {
    if (typeof window === 'undefined') return null;
    const raw = sessionStorage.getItem(CAMPAIGN_WORKSPACE_RESUME_ZONE_KEY)?.trim();
    return raw || null;
  } catch {
    return null;
  }
}

export function clearCampaignWorkspaceResumeIdFromSession(): void {
  try {
    sessionStorage.removeItem(CAMPAIGN_WORKSPACE_RESUME_ID_KEY);
    sessionStorage.removeItem(CAMPAIGN_WORKSPACE_RESUME_ZONE_KEY);
    sessionStorage.removeItem(CAMPAIGN_HOME_OVERLAY_KEY);
  } catch {
    /* noop */
  }
}

export function readCampaignHomeOverlayFromSession(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem(CAMPAIGN_HOME_OVERLAY_KEY) === '1';
  } catch {
    return false;
  }
}

export function setCampaignHomeOverlayInSession(active: boolean): void {
  try {
    if (active) {
      sessionStorage.setItem(CAMPAIGN_HOME_OVERLAY_KEY, '1');
    } else {
      sessionStorage.removeItem(CAMPAIGN_HOME_OVERLAY_KEY);
    }
  } catch {
    /* noop */
  }
}
